
import React, { useEffect, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Trash2, Upload } from 'lucide-react';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Poll } from '@/types/poll';

// Update the interface to accept either imageUrl+onChange OR currentPoll+updateField
interface PollImageUploadProps {
  imageUrl?: string | null;
  onChange?: (url: string | null) => void;
  currentPoll?: Poll;
  updateField?: (field: keyof Poll, value: any) => void;
}

const PollImageUpload: React.FC<PollImageUploadProps> = ({ 
  imageUrl: propImageUrl, 
  onChange,
  currentPoll,
  updateField
}) => {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Determine image URL from props
  const imageUrl = propImageUrl || (currentPoll?.image_url || null);

  // Set initial preview URL if imageUrl exists
  useEffect(() => {
    setPreviewUrl(imageUrl);
  }, [imageUrl]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file (JPEG, PNG, etc.)');
        return;
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }

      // Set local preview
      const localPreviewUrl = URL.createObjectURL(file);
      setPreviewUrl(localPreviewUrl);

      // Start upload
      setUploading(true);

      // Generate a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}_${Date.now()}.${fileExt}`;
      const filePath = `polls/${fileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('poll-images')
        .upload(filePath, file, { 
          cacheControl: '3600', 
          contentType: file.type 
        });

      if (error) {
        console.error('Error uploading image:', error);
        toast.error(`Upload failed: ${error.message}`);
        // Revert preview on error
        setPreviewUrl(imageUrl);
        return;
      }

      // Get public URL for the uploaded file
      const {
        data: { publicUrl },
      } = supabase.storage.from('poll-images').getPublicUrl(data.path);

      // Call appropriate handler based on which props were provided
      if (onChange) {
        onChange(publicUrl);
      } else if (currentPoll && updateField) {
        updateField('image_url', publicUrl);
      }
      
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error in image upload:', error);
      toast.error('Something went wrong during upload');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    // We'll not actually delete from storage for simplicity
    // Just remove the reference
    if (onChange) {
      onChange(null);
    } else if (currentPoll && updateField) {
      updateField('image_url', null);
    }
    setPreviewUrl(null);
    toast.success('Image removed');
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="poll-image">Poll Image (Optional)</Label>
      <div className="space-y-3">
        {previewUrl ? (
          <div className="relative w-full max-w-md">
            <AspectRatio ratio={3/2}>
              <img
                src={previewUrl}
                alt="Poll image preview"
                className="rounded-md object-cover w-full h-full"
              />
            </AspectRatio>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              onClick={handleRemoveImage}
              className="absolute top-2 right-2"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md p-6 w-full max-w-md">
            <div className="text-center">
              <Upload className="mx-auto h-12 w-12 text-gray-400" />
              <div className="mt-2 text-sm text-gray-500">
                <label htmlFor="poll-image" className="cursor-pointer font-medium text-blue-500 hover:text-blue-700">
                  Click to upload an image
                </label>
                <p className="text-xs mt-1">PNG, JPG, GIF up to 5MB</p>
              </div>
            </div>
          </div>
        )}

        <Input 
          id="poll-image"
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          disabled={uploading}
          className="sr-only"
        />

        {uploading && (
          <div className="flex items-center space-x-2">
            <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
            <span className="text-sm text-gray-500">Uploading...</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PollImageUpload;
